import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth as useOidcAuth } from 'react-oidc-context';
import { IdleTimeoutGuard } from '../auth/IdleTimeout';
import { FullPageLoading } from './FullPageLoading';

/** Where to land after sign-in — written here at the moment the user is bounced to /login. */
export const RETURN_TO_KEY = 'fo-return-to';

/**
 * A yes/no "a sponsored invite is pending" signal (steward-app#1649), set alongside
 * `RETURN_TO_KEY` when the fragment that bounced the user here carries a non-empty `#invite=`.
 * This key NEVER holds the token or the fragment, only the literal string `'1'` — see
 * `hasSponsoredInviteToken` below, which discards the parsed token immediately after testing
 * whether it is present. `/login` reads this to show the same "You have been invited" line an
 * ordinary `?invite=` bounce already gets (`Login.tsx`'s `returnedForInvite`), and unconditionally
 * clears it the moment it reads it, whatever the result. `Callback.tsx` also clears it
 * unconditionally on sign-in completion, belt-and-braces: `ProtectedRoute` is the only place that
 * sets this key, and `/login` is the only place that reads it, so that second clear has no known
 * live path today, but it costs nothing to keep and matches `RETURN_TO_KEY`'s own handling right
 * above it. Both
 * clears live inside `sessionStorage`, which does not survive the tab closing but DOES survive an
 * ordinary reload or a duplicated tab within that window.
 */
export const SPONSORED_INVITE_PENDING_KEY = 'fo-sponsored-invite-pending';

/**
 * True when `hash` is a sponsored invite's `#invite=<token>` fragment carrying a non-empty
 * token — the same non-empty test `SelectWorkspace.tsx`'s `inviteTokenFromHash` applies (a bare
 * `#invite` or `#invite=` with nothing after the `=` is not treated as a real invite by either).
 * Kept local rather than imported from `SelectWorkspace.tsx`: `ProtectedRoute` sits on every
 * protected route, and importing the `SelectWorkspace` page here would pull it out of its own
 * lazy chunk. The parsed token is used only for this boolean test and is never returned, stored,
 * or passed anywhere.
 */
function hasSponsoredInviteToken(hash: string): boolean {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  return !!new URLSearchParams(raw).get('invite');
}

export function ProtectedRoute({ children }: { children: ReactNode }) {
  const auth = useOidcAuth();
  const location = useLocation();
  if (auth.isLoading) return <FullPageLoading />;
  if (!auth.isAuthenticated) {
    try {
      // Path and query ONLY, never `location.hash` itself, and that is deliberate
      // (steward-app#1488): the sponsored invitation link carries its token in the fragment, and
      // the token is a workspace-admin grant that must not land in storage. A signed-out family
      // loses the fragment here and finds the same invitation in Select workspace's own list,
      // which `/me` fills.
      sessionStorage.setItem(RETURN_TO_KEY, location.pathname + location.search);
      // The fragment itself is still never persisted (steward-app#1649) — only whether it looked
      // like a sponsored invite. `hasSponsoredInviteToken` parses `location.hash` in memory and
      // returns a boolean; nothing derived from the token's VALUE crosses into storage.
      if (hasSponsoredInviteToken(location.hash)) {
        sessionStorage.setItem(SPONSORED_INVITE_PENDING_KEY, '1');
      }
    } catch {
      // ignore storage failures (e.g. private browsing)
    }
    return <Navigate to="/login" replace />;
  }
  return <IdleTimeoutGuard>{children}</IdleTimeoutGuard>;
}
